import { type NodeProps, Position } from '@xyflow/react';
import EditIcon from '@mui/icons-material/Edit';
import { v4 as uuidv4 } from 'uuid';

import type { NodeItem, ParentNode } from '@models/flows';
import { Connector, isAllTestGroupsSelected } from '@utils/flows';

import { useToast } from '@hooks/useToast';
import { useNodeError } from '@hooks/useNodeError';

import {
  AddButton,
  ConnectionPoint,
  DeleteButton,
  ErrorToolTip,
} from '@components/Flows/components/partials/index';
import {
  initialTestGroupsNode,
  isValueArray,
} from '@features/chargerFlows/validation/validation-helpers';

import s from './TestGroupsNode.module.css';

type TestGroupBlockProps = {
  nodeItem: NodeItem;
  onEdit: (code: string) => void;
};

const TestGroupBlock = ({
  nodeItem: { id, value, isAllSelected },
  onEdit,
}: TestGroupBlockProps) => {
  const renderValue = () => {
    if (isValueArray(value)) {
      if (isAllTestGroupsSelected(isAllSelected as boolean)) {
        return 'All';
      }

      return (value as string[]).join(', ');
    }

    return value;
  };

  return (
    <div className={s.testGroupBlock}>
      <div className={s.testGroupLineWrapper}>
        <p className={s.testGroupCode}>{renderValue()}</p>
      </div>
      <button type="button" className={s.editButton} onClick={() => onEdit(id)}>
        <EditIcon />
      </button>
      <ConnectionPoint type={Connector.Source} position={Position.Right} id={id} />
    </div>
  );
};

type TestGroupsProps = NodeProps & {
  onEdit: (parentNode: ParentNode, childNode: NodeItem) => void;
  onChange: (parentNode: ParentNode, childNode: NodeItem) => void;
  onDelete: (nodeId: string) => void;
};

const getNewTestGroupNodeItem = (): NodeItem => ({
  id: uuidv4(),
  value: [''],
  isAllSelected: false,
});

function TestGroupsNode({ id, type, data, onEdit, onChange, onDelete }: TestGroupsProps) {
  const toast = useToast();
  const testGroupsData = data as unknown as { items: NodeItem[] };
  const nodeItems = testGroupsData?.items || [initialTestGroupsNode];
  const nodeId = id;
  const parentNode: ParentNode = {
    id: nodeId,
    type,
    data: testGroupsData,
  };
  const errors = useNodeError(nodeId);

  const handleNewNode = () => {
    onChange(parentNode, getNewTestGroupNodeItem());
  };

  const handleEditNode = (nodeItemId: string) => {
    const editableItem = nodeItems.find((item) => item.id === nodeItemId);

    if (!editableItem) {
      toast.error('Test group item not found');
      return;
    }

    onEdit(parentNode, editableItem);
  };

  return (
    <div className={`${s.testGroupsContainer} ${errors ? s.errorBorder : ''}`}>
      <ConnectionPoint type={Connector.Target} position={Position.Left} id="incoming" />
      <h1 className={s.title}>Test Groups Node</h1>
      <ErrorToolTip isErrorVisible={Boolean(errors)} messages={errors?.messages} />
      <DeleteButton onDelete={() => onDelete(nodeId)} />
      <div className={s.testGroupsList}>
        {nodeItems.length > 0 &&
          nodeItems.map((item) => (
            <TestGroupBlock key={item.id} nodeItem={item} onEdit={handleEditNode} />
          ))}
      </div>
      <AddButton handleClick={handleNewNode} />
    </div>
  );
}

export default TestGroupsNode;
